/**
 * Visor de la bitacora de fallos.
 *
 * POR QUE HACE FALTA UN VISOR
 * ---------------------------
 * `lib/error-log.ts` apunta cada fallo de la pasarela, pero lo apunta en el
 * aparato. En la tablet del mostrador y en el iPhone de la dueña **no hay
 * consola**, asi que sin esto la bitacora es una herramienta de diagnostico que
 * nadie puede alcanzar — justo el defecto que venia a resolver.
 *
 * El caso de uso real: la dueña dice "algo no jala". En vez de pedirle que
 * describa una pantalla vacia, se enciende `?diag=1` una vez, se toca la
 * pastilla y ahi esta el `PGRST201` con su ruta y su hora. Y con **Copiar** lo
 * manda por WhatsApp sin transcribir nada.
 *
 * SE ENCIENDE CON `?diag=1`, IGUAL QUE LA SONDA DEL MARCO
 * ------------------------------------------------------
 * La bandera vive en `main.tsx` y ya resuelve las dos trampas: el router de la
 * casa reescribe la direccion y borraria el parametro, y la PWA instalada
 * arranca siempre en su `start_url` sin admitir parametros. Por eso queda
 * anotada en `localStorage`. Se apaga con `?diag=0`.
 *
 * A DIFERENCIA DE `ViewportDiag`, ESTE NO ES TEMPORAL. Aquella sonda existe para
 * cerrar un caso concreto de iOS y se borra despues; esta es la unica ventana
 * que hay a lo que falla en un aparato que no se puede conectar a un depurador.
 *
 * Va abajo a la derecha para no encimarse con la sonda del marco, que ocupa la
 * franja de arriba. Y lleva estilos EN LINEA a proposito: tiene que verse igual
 * aunque lo que este roto sea la hoja de estilos.
 */
import { useEffect, useState } from 'react';
import { readApiFailures, clearApiFailures, type FalloApi } from '../lib/error-log.js';

function hora(ms: number): string {
  const d = new Date(ms);
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

/** Una linea por fallo, en el formato que se pega en un mensaje. */
function comoTexto(fallos: FalloApi[]): string {
  return fallos
    .map(
      (f) =>
        `${hora(f.at)}  ${f.method} ${f.path}  ${f.code}` +
        (f.pg ? ` (${f.pg})` : '') +
        ` [${f.status}]` +
        (f.queryKeys?.length ? `  q:${f.queryKeys.join(',')}` : ''),
    )
    .join('\n');
}

export function ErrorLogDiag(): React.ReactNode {
  const [fallos, setFallos] = useState<FalloApi[]>([]);
  const [abierto, setAbierto] = useState(false);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    const actualizar = (): void => setFallos(readApiFailures());
    actualizar();
    // Se relee en vez de escuchar un evento: la bitacora la escribe `api.ts` sin
    // avisarle a nadie, y un intervalo de un segundo cuesta nada al lado de
    // acoplar la pasarela a un componente de diagnostico.
    const t = window.setInterval(actualizar, 1000);
    return () => window.clearInterval(t);
  }, []);

  const copiar = (): void => {
    void navigator.clipboard?.writeText(comoTexto(fallos)).then(
      () => {
        setCopiado(true);
        window.setTimeout(() => setCopiado(false), 1500);
      },
      () => {
        // Sin permiso de portapapeles no se puede hacer mas; el texto sigue en
        // pantalla para leerlo o capturarlo.
      },
    );
  };

  const hay = fallos.length > 0;

  return (
    <div
      style={{
        position: 'fixed',
        right: '8px',
        bottom: `calc(8px + var(--safe-b, 0px))`,
        zIndex: 2147483646, // Uno menos que la sonda del marco.
        font: '600 11px/1.35 ui-monospace, SFMono-Regular, Menlo, monospace',
        color: '#fff',
        maxWidth: 'min(92vw, 30rem)',
      }}
    >
      {abierto ? (
        <div
          style={{
            background: 'rgba(15,15,18,0.97)',
            border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: '10px',
            padding: '8px',
            marginBottom: '6px',
          }}
        >
          <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
            <button
              type="button"
              onClick={copiar}
              disabled={!hay}
              style={{
                flex: 1,
                padding: '8px',
                borderRadius: '7px',
                border: 0,
                background: hay ? '#2563eb' : '#3f3f46',
                color: '#fff',
                font: 'inherit',
              }}
            >
              {copiado ? 'Copiado' : 'Copiar'}
            </button>
            <button
              type="button"
              onClick={() => {
                clearApiFailures();
                setFallos([]);
              }}
              disabled={!hay}
              style={{
                flex: 1,
                padding: '8px',
                borderRadius: '7px',
                border: 0,
                background: hay ? '#7f1d1d' : '#3f3f46',
                color: '#fff',
                font: 'inherit',
              }}
            >
              Borrar
            </button>
          </div>

          <div style={{ maxHeight: '40vh', overflowY: 'auto', overflowX: 'auto' }}>
            {hay ? (
              // Del mas reciente al mas viejo: lo que acaba de fallar es lo que
              // se esta buscando.
              [...fallos].reverse().map((f, i) => (
                <div
                  key={`${f.at}-${i}`}
                  style={{
                    padding: '4px 0',
                    borderTop: i === 0 ? 0 : '1px solid rgba(255,255,255,0.12)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span style={{ opacity: 0.65 }}>{hora(f.at)}</span>{' '}
                  <span style={{ color: '#fca5a5' }}>
                    {f.pg ?? f.code}
                  </span>{' '}
                  <span style={{ opacity: 0.9 }}>
                    {f.method} {f.path}
                  </span>
                </div>
              ))
            ) : (
              <div style={{ opacity: 0.7, padding: '4px 0' }}>Sin fallos apuntados.</div>
            )}
          </div>
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        style={{
          display: 'block',
          marginLeft: 'auto',
          padding: '7px 11px',
          borderRadius: '999px',
          border: 0,
          background: hay ? 'rgba(190,20,20,0.96)' : 'rgba(10,90,40,0.96)',
          color: '#fff',
          font: 'inherit',
        }}
      >
        {hay ? `${fallos.length} fallo${fallos.length === 1 ? '' : 's'}` : 'sin fallos'}
      </button>
    </div>
  );
}
